import { lazy, Suspense, useEffect } from "react";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { SvgFilters } from "@/components/ui/svg-filters";
import { GlobalDrawer } from "@/components/drawer/global-drawer";
import { Routes, Route, useLocation } from "react-router";
import { HomePage } from "@/pages/HomePage";
import { SmoothScrollProvider } from "@/components/providers/smooth-scroll-provider";
import { ScrollToHash } from "@/components/navigation/scroll-to-hash";

// Route-level code splitting — only HomePage is eagerly loaded for
// fast initial paint. All other routes load on navigation.
const CaseStudyDetailPage = lazy(() => import("@/pages/CaseStudyDetailPage").then(m => ({ default: m.CaseStudyDetailPage })));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage").then(m => ({ default: m.PrivacyPolicyPage })));
const ProductPage = lazy(() => import("@/pages/ProductPage").then(m => ({ default: m.ProductPage })));
const SolutionPage = lazy(() => import("@/pages/SolutionPage").then(m => ({ default: m.SolutionPage })));
const ComparePage = lazy(() => import("@/pages/ComparePage").then(m => ({ default: m.ComparePage })));
const WorkersRosterPage = lazy(() => import("@/pages/WorkersRosterPage").then(m => ({ default: m.WorkersRosterPage })));
const WorkerProfilePage = lazy(() => import("@/pages/WorkerProfilePage").then(m => ({ default: m.WorkerProfilePage })));
const TeamsRosterPage = lazy(() => import("@/pages/TeamsRosterPage").then(m => ({ default: m.TeamsRosterPage })));
const TeamProfilePage = lazy(() => import("@/pages/TeamProfilePage").then(m => ({ default: m.TeamProfilePage })));

// Single responsive app — sections use Tailwind responsive utilities.
// The legacy MobileHomePage, *-mobile section duplicates, and MobileApp
// branch were removed May 16 2026; HomePage is the single home route.
function App() {
  const location = useLocation();

  // Track SPA route changes in GA4 + Meta Pixel
  useEffect(() => {
    window.PolyAnalytics?.trackPageView();
  }, [location.pathname]);

  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <SvgFilters />
      <SmoothScrollProvider>
        <ScrollToHash />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/workers" element={<WorkersRosterPage />} />
            <Route path="/workers/:slug" element={<WorkerProfilePage />} />
            <Route path="/teams" element={<TeamsRosterPage />} />
            <Route path="/teams/:slug" element={<TeamProfilePage />} />
            <Route path="/products/:slug" element={<ProductPage />} />
            <Route path="/for/:slug" element={<SolutionPage />} />
            <Route path="/compare/:slug" element={<ComparePage />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          </Routes>
        </Suspense>
      </SmoothScrollProvider>
      <GlobalDrawer />
    </ThemeProvider>
  );
}

export default App;
